// ============================================================
// sale-api.js
// ------------------------------------------------------------
// ตัวเรียก API ของหน้าเว็บสาธารณะ
//
// ★ ทำไมไม่ใช้ GET/POST แบบหลังบ้าน (CustomComponent.js เดิม — ลบออกจาก repo นี้แล้ว)
//   1. ตัวนั้นแนบ header Authorization จาก token ที่เก็บไว้ — หน้านี้ไม่มีใคร login
//   2. ตัวนั้นต่อ query string เองโดยไม่ encode ค่า — ค่าที่เป็นภาษาไทย
//      (เช่น listing_type=ขาย) จะส่งไปไม่ถึง backend
//   ตัวนี้จึงใช้ URLSearchParams และไม่แนบ token
//
// project_id เป็นตัวเลือก — ไม่ส่ง = ดูทุกโครงการ
// ยกเว้นฟอร์มนัดดูห้องที่ backend บังคับต้องมี
// ============================================================

const BASE = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");

const buildUrl = (path, params) => {
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;
        query.append(key, value);
    });
    const qs = query.toString();
    return `${BASE}/api/public/sale${path}${qs ? `?${qs}` : ""}`;
};

// คืน { ok, data, message, raw } เสมอ ไม่โยน exception ให้หน้าจอต้องดักเอง
// ★ ไม่ใส่ cache: "no-cache" แล้ว — ตัวนั้นบังคับให้เบราว์เซอร์ถาม server ใหม่ทุกครั้ง
//   แม้ server จะอนุญาตให้ cache ได้ก็ตาม ปล่อยให้ header ของ backend เป็นคนตัดสิน
// ★ GET ไม่ส่ง Content-Type — ไม่มี body อยู่แล้ว และ header นี้ทำให้ทุก request ต้องยิง
//   preflight (OPTIONS) เพิ่มอีกรอบเพราะเป็นคนละโดเมนกับ API
const request = async (url, options = {}) => {
    try {
        const response = await fetch(url, {
            ...options,
            headers: {
                accept: "application/json",
                ...(options.body ? { "Content-Type": "application/json" } : {}),
            },
        });
        const result = await response.json();
        return {
            ok: !!result.status,
            data: result.data ?? null,
            message: result.message || "",
            raw: result,
        };
    } catch (error) {
        console.error("sale api error:", error);
        return { ok: false, data: null, message: "เชื่อมต่อระบบไม่สำเร็จ กรุณาลองใหม่อีกครั้ง" };
    }
};

// รายชื่อโครงการที่มีห้องเปิดประกาศ (ใช้ทำตัวกรอง)
export const fetchProjects = () => request(buildUrl("/projects"));

// รายการห้องประกาศขาย/เช่า — ไม่ส่ง project_id = ทุกโครงการ
// ★ คืนรูปเดียวกันเสมอ : data = { data: [ห้อง], project, count, has_next }
//   backend ตอบคนละรูปตาม page — page=0 ห่อไว้ใน data, page>=1 วาง count/has_next ไว้ชั้นนอก
//   (ตัว request ด้านบนหยิบแค่ result.data จึงต้องประกอบกลับที่นี่ ไม่งั้น count/has_next หาย)
export const fetchListings = async (params) => {
    const res = await request(buildUrl("/listings", params));
    if (!res.ok) return res;
    const raw = res.raw || {};
    const body = Array.isArray(res.data) ? { data: res.data } : res.data || {};
    const rows = body.data || [];
    return {
        ...res,
        data: {
            data: rows,
            project: body.project ?? raw.project ?? null,
            count: Number(body.count ?? raw.count ?? rows.length) || 0,
            has_next: body.has_next ?? raw.has_next,
        },
    };
};

// รายละเอียดห้อง 1 ห้อง (backend จะนับยอดเข้าชมให้เอง · track=0 = ไม่นับ ใช้กับ bot/พรีวิวลิงก์)
// ไม่ส่ง project_id ก็ได้ — backend จะใช้โครงการของห้องนั้นเอง
// ★ encode uid — uid มาจาก query string ของลิงก์ที่แชร์กัน ใส่ / หรือ ? มาจะกลายเป็น path อื่น
export const fetchListingDetail = (uid, projectId) =>
    request(buildUrl(`/listings/${encodeURIComponent(uid)}`, { project_id: projectId }));

// เนื้อหาหน้าเว็บที่ผู้ดูแลตั้งเอง (hero / จุดขาย / แกลเลอรี / รีวิว / FAQ / ติดต่อ)
// ไม่ส่ง project_id = ได้ "เนื้อหากลาง" ที่ใช้ตอนแสดงรวมทุกโครงการ
export const fetchPageContent = (projectId) =>
    request(buildUrl("/page-content", { project_id: projectId }));

// ส่งฟอร์มนัดดูห้อง
export const submitInquiry = (payload) =>
    request(buildUrl("/inquiries"), { method: "POST", body: JSON.stringify(payload) });

// อ่านรหัสโครงการจาก URL — รองรับทั้ง ?project= และ ?project_id=
export const getProjectIdFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return (params.get("project") || params.get("project_id") || "").trim();
};
